import type { MoveResult, ProjectFieldBinding } from '@servicetim/shared';
import { useFieldBindings, useIssueTypes, useMoveTicket, useProjects } from '@servicetim/ui/api';
import {
  Button,
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
  Label,
  ProblemBanner,
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
  toast,
} from '@servicetim/ui/components';
import { useNavigate } from '@tanstack/react-router';
import { ArrowRight, LoaderCircle } from 'lucide-react';
import { useEffect, useId, useState } from 'react';
import { useProjectStatuses } from './api';
import { useTicketContext } from './ticket-context';

const NONE = '__none__';

/**
 * Move to another project (S-05-12): target project → issue type → optional status → a mapping
 * row for every custom field whose key the target does not bind. The result lists what stayed
 * unmapped (`external_ref.unmapped`).
 */
export function MoveDialog({
  open,
  onOpenChange,
}: {
  open: boolean;
  onOpenChange: (open: boolean) => void;
}) {
  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent size="lg" aria-describedby={undefined}>
        {open ? <MoveBody onOpenChange={onOpenChange} /> : null}
      </DialogContent>
    </Dialog>
  );
}

function MoveBody({ onOpenChange }: { onOpenChange: (open: boolean) => void }) {
  const { ticket } = useTicketContext();
  const navigate = useNavigate();
  const projects = useProjects({ pageSize: 200 });
  const [projectId, setProjectId] = useState<string | undefined>(undefined);
  const issueTypes = useIssueTypes(projectId);
  const [issueTypeId, setIssueTypeId] = useState<string | undefined>(undefined);
  const [statusId, setStatusId] = useState<string>(NONE);
  const statuses = useProjectStatuses(projectId);
  const sourceBindings = useFieldBindings(ticket.projectId);
  const targetBindings = useFieldBindings(projectId);
  const [mapping, setMapping] = useState<Record<string, string>>({});
  const [result, setResult] = useState<MoveResult | null>(null);
  const move = useMoveTicket();
  const projectSelectId = useId();
  const typeSelectId = useId();
  const statusSelectId = useId();

  useEffect(() => {
    if (!issueTypes.data) return;
    setIssueTypeId((current) =>
      current && issueTypes.data.some((t) => t.id === current)
        ? current
        : (issueTypes.data.find((t) => !t.isSubtask) ?? issueTypes.data[0])?.id,
    );
  }, [issueTypes.data]);

  const workflowId = issueTypes.data?.find((t) => t.id === issueTypeId)?.workflowId;
  const statusOptions = statuses.statuses.filter(
    (s) => workflowId === undefined || s.workflowId === workflowId,
  );

  const targetKeys = new Set((targetBindings.data ?? []).map((b) => b.fieldKey));
  const applies = (b: ProjectFieldBinding) =>
    b.issueTypeIds === null || issueTypeId === undefined || b.issueTypeIds.includes(issueTypeId);
  const unmappedSource = (sourceBindings.data ?? []).filter(
    (b) => ticket.fields[b.fieldKey] !== undefined && !targetKeys.has(b.fieldKey),
  );
  const targetCandidates = (type: string) =>
    (targetBindings.data ?? []).filter((b) => b.fieldType === type && applies(b));
  const used = new Set(Object.values(mapping));

  const submit = async () => {
    if (!projectId || !issueTypeId) return;
    const fieldMapping: Record<string, string> = {};
    for (const [from, to] of Object.entries(mapping))
      if (to && to !== NONE) fieldMapping[from] = to;
    const moved = await move.mutateAsync({
      id: ticket.id,
      projectId,
      issueTypeId,
      statusId: statusId === NONE ? undefined : statusId,
      fieldMapping,
    });
    if (moved.unmapped.length === 0) {
      toast.success(`Moved to ${moved.ticket.key}`);
      onOpenChange(false);
      void navigate({ to: '/tickets/$idOrKey', params: { idOrKey: moved.ticket.key } });
    } else {
      setResult(moved);
    }
  };

  if (result) {
    return (
      <>
        <DialogHeader>
          <DialogTitle>Moved to {result.ticket.key}</DialogTitle>
          <DialogDescription>
            {result.unmapped.length} value{result.unmapped.length === 1 ? '' : 's'} had no field in
            the target project and were kept in the ticket's external reference.
          </DialogDescription>
        </DialogHeader>
        <ul className="list-disc space-y-1 pl-5 text-sm">
          {result.unmapped.map((u) => (
            <li key={u.fieldKey}>
              <span className="font-medium">{u.label}</span>{' '}
              <code className="font-mono text-fg-muted text-xs">({u.fieldKey})</code>:{' '}
              {typeof u.value === 'object' ? JSON.stringify(u.value) : String(u.value)}
            </li>
          ))}
        </ul>
        <DialogFooter>
          <Button
            onClick={() => {
              onOpenChange(false);
              void navigate({ to: '/tickets/$idOrKey', params: { idOrKey: result.ticket.key } });
            }}
          >
            Open {result.ticket.key}
            <ArrowRight />
          </Button>
        </DialogFooter>
      </>
    );
  }

  return (
    <>
      <DialogHeader>
        <DialogTitle>Move {ticket.key}</DialogTitle>
        <DialogDescription>
          The ticket keeps its id and history and gets a new key in the target project; the old key
          keeps resolving.
        </DialogDescription>
      </DialogHeader>
      <div className="grid gap-4 sm:grid-cols-3">
        <div className="space-y-1.5">
          <Label htmlFor={projectSelectId}>Target project</Label>
          <Select
            value={projectId ?? ''}
            onValueChange={(v) => {
              setProjectId(v);
              setIssueTypeId(undefined);
              setStatusId(NONE);
              setMapping({});
            }}
          >
            <SelectTrigger id={projectSelectId}>
              <SelectValue placeholder={projects.isPending ? 'Loading…' : 'Select…'} />
            </SelectTrigger>
            <SelectContent>
              {projects.items
                .filter((p) => p.id !== ticket.projectId && p.archivedAt === null)
                .map((p) => (
                  <SelectItem key={p.id} value={p.id}>
                    {p.key} · {p.name}
                  </SelectItem>
                ))}
            </SelectContent>
          </Select>
        </div>
        <div className="space-y-1.5">
          <Label htmlFor={typeSelectId}>Issue type</Label>
          <Select value={issueTypeId ?? ''} onValueChange={setIssueTypeId} disabled={!projectId}>
            <SelectTrigger id={typeSelectId}>
              <SelectValue placeholder="Select…" />
            </SelectTrigger>
            <SelectContent>
              {(issueTypes.data ?? []).map((t) => (
                <SelectItem key={t.id} value={t.id}>
                  {t.name}
                </SelectItem>
              ))}
            </SelectContent>
          </Select>
        </div>
        <div className="space-y-1.5">
          <Label htmlFor={statusSelectId}>Status</Label>
          <Select value={statusId} onValueChange={setStatusId} disabled={!issueTypeId}>
            <SelectTrigger id={statusSelectId}>
              <SelectValue />
            </SelectTrigger>
            <SelectContent>
              <SelectItem value={NONE}>Initial status of the workflow</SelectItem>
              {statusOptions.map((s) => (
                <SelectItem key={s.id} value={s.id}>
                  {s.name}
                </SelectItem>
              ))}
            </SelectContent>
          </Select>
        </div>
      </div>

      {projectId && targetBindings.data && unmappedSource.length > 0 ? (
        <div className="space-y-2">
          <p className="text-fg-muted text-sm">
            These fields have no counterpart with the same key in the target. Map them to a field of
            the same type or leave them unmapped.
          </p>
          <div className="overflow-hidden rounded-md border border-border">
            <Table>
              <TableHeader>
                <TableRow>
                  <TableHead>Source field</TableHead>
                  <TableHead>Value</TableHead>
                  <TableHead>Target field</TableHead>
                </TableRow>
              </TableHeader>
              <TableBody>
                {unmappedSource.map((b) => {
                  const candidates = targetCandidates(b.fieldType);
                  const value = ticket.fields[b.fieldKey];
                  return (
                    <TableRow key={b.fieldKey}>
                      <TableCell>
                        {b.fieldLabel}
                        <span className="ml-1 text-fg-muted text-xs">{b.fieldType}</span>
                      </TableCell>
                      <TableCell className="max-w-48 truncate text-fg-muted">
                        {ticket.display[b.fieldKey] ??
                          (typeof value === 'object' ? JSON.stringify(value) : String(value))}
                      </TableCell>
                      <TableCell>
                        <Select
                          value={mapping[b.fieldKey] ?? NONE}
                          onValueChange={(v) => setMapping({ ...mapping, [b.fieldKey]: v })}
                        >
                          <SelectTrigger size="sm" aria-label={`Target for ${b.fieldLabel}`}>
                            <SelectValue />
                          </SelectTrigger>
                          <SelectContent>
                            <SelectItem value={NONE}>Leave unmapped</SelectItem>
                            {candidates.map((c) => (
                              <SelectItem
                                key={c.fieldKey}
                                value={c.fieldKey}
                                disabled={
                                  used.has(c.fieldKey) && mapping[b.fieldKey] !== c.fieldKey
                                }
                              >
                                {c.fieldLabel}
                              </SelectItem>
                            ))}
                          </SelectContent>
                        </Select>
                      </TableCell>
                    </TableRow>
                  );
                })}
              </TableBody>
            </Table>
          </div>
        </div>
      ) : null}
      {move.error ? <ProblemBanner error={move.error} size="sm" /> : null}
      <DialogFooter>
        <Button variant="outline" onClick={() => onOpenChange(false)} disabled={move.isPending}>
          Cancel
        </Button>
        <Button
          onClick={() => void submit()}
          disabled={!projectId || !issueTypeId || move.isPending}
        >
          {move.isPending ? <LoaderCircle className="animate-spin" /> : null}
          Move ticket
        </Button>
      </DialogFooter>
    </>
  );
}
